import { useState, useEffect, useCallback, useRef } from 'react';

const LOCAL_DEV_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);

const isLocalDevHostname = () => {
  if (process.env.GATSBY_DISABLE_LOCAL_RECAPTCHA_BYPASS === 'true') {
    return false;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  return LOCAL_DEV_HOSTNAMES.has(window.location.hostname);
};

export const useRecaptcha = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const scriptLoadingRef = useRef(false);
  const scriptLoadedRef = useRef(false);

  const loadRecaptchaScript = useCallback(() => {
    // If already loaded or loading, don't load again
    if (scriptLoadedRef.current || scriptLoadingRef.current) {
      return;
    }

    // Check if script already exists in DOM
    const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
    if (existingScript) {
      scriptLoadedRef.current = true;
      return;
    }

    const siteKey = process.env.GATSBY_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      setError('reCAPTCHA site key not configured');
      return;
    }

    scriptLoadingRef.current = true;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      scriptLoadingRef.current = false;
      scriptLoadedRef.current = true;

      // Wait for grecaptcha to be ready
      const checkReady = () => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            setIsLoaded(true);
            setError(null);
          });
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    };

    script.onerror = () => {
      scriptLoadingRef.current = false;
      setError('Failed to load reCAPTCHA script');
    };

    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (isLocalDevHostname()) {
      scriptLoadedRef.current = true;
      setIsLoaded(true);
      setError(null);
      return;
    }

    // Check if reCAPTCHA is already loaded (in case script was added elsewhere)
    if (window.grecaptcha && window.grecaptcha.ready) {
      scriptLoadedRef.current = true;
      setIsLoaded(true);
    }
  }, []);

  const executeRecaptcha = useCallback(
    async (action = 'submit') => {
      if (isLocalDevHostname()) {
        setError(null);
        return `local-dev-${action}`;
      }

      const siteKey = process.env.GATSBY_RECAPTCHA_SITE_KEY;
      if (!siteKey) {
        const err = new Error('reCAPTCHA site key not configured');
        setError(err.message);
        throw err;
      }

      // Load the script if it hasn't been loaded yet
      if (!scriptLoadedRef.current && !scriptLoadingRef.current) {
        loadRecaptchaScript();
      }

      // Wait for the script to load
      if (!isLoaded) {
        return new Promise((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 50; // 5 seconds max wait

          const checkLoaded = () => {
            attempts++;

            if (window.grecaptcha && window.grecaptcha.ready) {
              window.grecaptcha.ready(async () => {
                try {
                  const token = await window.grecaptcha.execute(siteKey, { action });
                  resolve(token);
                } catch (err) {
                  setError(err.message);
                  reject(err);
                }
              });
            } else if (attempts >= maxAttempts) {
              const err = new Error('reCAPTCHA loading timeout');
              setError(err.message);
              reject(err);
            } else {
              setTimeout(checkLoaded, 100);
            }
          };

          checkLoaded();
        });
      }

      // Script is already loaded
      if (!window.grecaptcha) {
        throw new Error('reCAPTCHA not available');
      }

      try {
        return await window.grecaptcha.execute(siteKey, { action });
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [isLoaded, loadRecaptchaScript],
  );

  return {
    isLoaded,
    error,
    executeRecaptcha,
  };
};
